/**
 * Calendar — the family calendar, editable in the app.
 *
 * Events are added, edited, and deleted right here (native events); an optional Google
 * sync can pull in a read-only mirror on top, but nothing depends on it. Month grid for
 * orientation, agenda for what's actually happening (the agenda is what gets read on a
 * phone in the kitchen). Events are colored by the family member they map to, using
 * family_members.color out of D1 — never a hardcoded palette.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type {
  CalendarEvent,
  CalendarStatus,
  CalendarSyncResult,
  MemberSummary,
} from '../../shared/types';
import { api, ApiRequestError } from '../../lib/api';
import MonthGrid from './MonthGrid';
import AgendaList, { MemberLegend, eventsOnDay } from './AgendaList';
import EventForm from './EventForm';
import {
  addDays,
  addMonths,
  compareEvents,
  dayKey,
  formatDayLabel,
  formatRelative,
  groupByDay,
  monthGridDays,
  monthLabel,
  startOfMonth,
  todayKey,
  upcomingEvents,
} from './lib';

/** How far ahead the "Up next" agenda looks. */
const AGENDA_DAYS = 45;

export default function CalendarPage() {
  const [month, setMonth] = useState(() => startOfMonth(new Date()));
  const [selectedDay, setSelectedDay] = useState<string | null>(null);

  const [status, setStatus] = useState<CalendarStatus | null>(null);
  const [members, setMembers] = useState<MemberSummary[]>([]);
  const [monthEvents, setMonthEvents] = useState<CalendarEvent[]>([]);
  const [agendaEvents, setAgendaEvents] = useState<CalendarEvent[]>([]);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [syncing, setSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState<CalendarSyncResult | null>(null);

  // Add/edit event form. `formState` is null when closed; { event } edits an existing
  // event, { event: null, date } creates one (optionally pre-dated from a chosen day).
  const [formState, setFormState] = useState<{ event: CalendarEvent | null; date: string | null } | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const openAdd = useCallback(
    (date: string | null) => {
      setActionError(null);
      setFormState({ event: null, date });
    },
    []
  );
  const openEdit = useCallback((e: CalendarEvent) => {
    setActionError(null);
    setFormState({ event: e, date: null });
  }, []);

  /**
   * Which load is the current one. Paging months on a phone is faster than a round trip, so
   * without this a slow answer for March can land AFTER April's and leave the April grid
   * showing March's events — a calendar that quietly lies about what day something is on.
   * Every load takes a ticket; only the newest ticket is allowed to write to state.
   */
  const loadSeq = useRef(0);

  const load = useCallback(async () => {
    const seq = ++loadSeq.current;
    setLoading(true);
    const grid = monthGridDays(month);
    const from = dayKey(grid[0]);
    const to = dayKey(grid[grid.length - 1]);
    const agendaTo = dayKey(addDays(new Date(), AGENDA_DAYS));

    try {
      const [nextStatus, nextMembers, nextMonth, nextAgenda] = await Promise.all([
        api.get<CalendarStatus>('/calendar/status'),
        api.get<MemberSummary[]>('/calendar/members'),
        api.get<CalendarEvent[]>(`/calendar?from=${from}&to=${to}`),
        api.get<CalendarEvent[]>(`/calendar?from=${todayKey()}&to=${agendaTo}`),
      ]);
      if (seq !== loadSeq.current) return; // superseded — this data is for a month we left
      setStatus(nextStatus);
      setMembers(nextMembers);
      setMonthEvents(nextMonth);
      setAgendaEvents(nextAgenda);
      setError(null);
    } catch (err) {
      if (seq !== loadSeq.current) return; // a newer load owns the screen; don't hijack it
      setError(
        err instanceof ApiRequestError
          ? err.message
          : "The calendar didn't load. Check your connection and try again."
      );
    } finally {
      // Only the newest load may clear the spinner, or a stale response would call the
      // screen "loaded" while the real one is still in flight.
      if (seq === loadSeq.current) setLoading(false);
    }
  }, [month]);

  useEffect(() => {
    void load();
  }, [load]);

  /**
   * Moving to another month drops the day selection. The selected day belongs to the month
   * whose events are loaded; changing month refetches monthEvents, so a day held over from
   * the old month would headline the agenda with a day the loaded data can't speak for —
   * "Nothing on the books" over a day that genuinely has events. One handler for all three
   * nav controls so a new one can't forget.
   */
  const goToMonth = useCallback((next: Date) => {
    setMonth(next);
    setSelectedDay(null);
  }, []);

  /**
   * The sync button, on every screen that has one — including the not-connected screen,
   * which is the ONLY screen that renders until Google is wired up.
   *
   * If the POST throws (500, offline, api_unreachable) there is no CalendarSyncResult to
   * show, so the reason lands in `error` — and `error` MUST be rendered wherever this
   * button is. Previously it wasn't: on the not-connected screen the button flipped back to
   * "Try the connection" and said nothing at all, forever.
   *
   * Both outcomes are cleared up front so a fresh press never shows the last press's answer.
   */
  async function handleSync() {
    setSyncing(true);
    setSyncResult(null);
    setError(null);
    try {
      const result = await api.post<CalendarSyncResult>('/calendar/sync');
      setSyncResult(result);
      await load();
    } catch (err) {
      // We know the sync didn't run. We do NOT know why unless the API told us — so we say
      // exactly what we have, and never guess at a cause.
      setError(
        err instanceof ApiRequestError
          ? `The sync couldn't run: ${err.message}`
          : "The sync couldn't run, and the app didn't get a reason back. Check your connection and try again."
      );
    } finally {
      setSyncing(false);
    }
  }

  async function handleDelete(ev: CalendarEvent) {
    if (!window.confirm(`Remove "${ev.title}" from the calendar?`)) return;
    setActionError(null);
    try {
      await api.del(`/calendar/events/${ev.id}`);
      await load();
    } catch (err) {
      setActionError(
        err instanceof ApiRequestError
          ? `Couldn't remove that: ${err.message}`
          : "Couldn't remove that event. Check your connection and try again."
      );
    }
  }

  const eventsByDay = useMemo(() => groupByDay(monthEvents), [monthEvents]);
  const upcoming = useMemo(() => upcomingEvents(agendaEvents), [agendaEvents]);
  const dayEvents = useMemo(
    () => (selectedDay ? eventsOnDay(monthEvents, selectedDay).sort(compareEvents) : []),
    [monthEvents, selectedDay]
  );

  // ---- loading: first paint only; a month change swaps data in place ----
  if (loading && !status) return <CalendarSkeleton />;

  // ---- error with nothing to fall back on ----
  if (error && !status) {
    return (
      <section className="rounded-card border border-border bg-surface p-8 text-center">
        <h1 className="font-display text-xl font-semibold text-ink">Calendar</h1>
        <p className="mx-auto mt-2 max-w-sm text-ink-soft">{error}</p>
        <button
          type="button"
          onClick={() => void load()}
          className="mt-5 inline-flex min-h-[2.75rem] items-center rounded-full bg-sage px-5 text-sm font-medium text-white hover:opacity-90 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sage"
        >
          Try again
        </button>
      </section>
    );
  }

  const failedCalendars = syncResult?.calendars.filter((cal) => !cal.ok) ?? [];
  // A calendar that WAS read but had more events than one sync can take in. Not a failure —
  // the events it did read are real and current — but not the whole truth either, and the
  // mirror deliberately removed nothing from it. It gets its own voice, so it can't be read
  // as either a clean success or a breakage.
  const partialCalendars = syncResult?.calendars.filter((cal) => cal.ok && cal.error) ?? [];
  // Configured but broken (a wrong key, or a calendar nobody shared with the robot) is a
  // failure just as much as an unreadable calendar is. Both must look like one.
  const syncFailed = !!syncResult && (failedCalendars.length > 0 || !syncResult.configured);

  const connected = status?.configured ?? false;
  // Has this calendar ever pulled from Google? The Worker persists the sync timestamp in
  // `settings`, so this stays true after a successful sync that returned zero events —
  // which is exactly the case where counting mirrored rows would call it "never synced".
  const everSynced = !!status?.last_synced_at;

  return (
    <div className="space-y-6">
      <Header status={status} syncing={syncing} onSync={() => void handleSync()} showSync />

      <div className="flex justify-end">
        <button
          type="button"
          onClick={() => openAdd(selectedDay)}
          className="inline-flex min-h-[2.75rem] items-center gap-1.5 rounded-full bg-sage px-4 text-sm font-medium text-white hover:opacity-90 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sage"
        >
          <span aria-hidden="true" className="text-base leading-none">+</span> Add event
        </button>
      </div>

      {actionError && (
        <p role="alert" className="rounded-card border border-clay bg-clay-soft px-4 py-3 text-sm text-clay">
          {actionError}
        </p>
      )}

      {formState && (
        <EventForm
          event={formState.event}
          defaultDate={formState.date}
          members={members}
          onClose={() => setFormState(null)}
          onSaved={() => {
            setFormState(null);
            void load();
          }}
        />
      )}

      {error && (
        <p
          role="alert"
          className="flex flex-wrap items-center gap-3 rounded-card border border-clay bg-clay-soft px-4 py-3 text-sm text-ink"
        >
          <span className="font-medium text-clay">Something went wrong.</span>
          {error}
          <button
            type="button"
            onClick={() => void load()}
            className="ml-auto min-h-[2.25rem] rounded-full border border-clay px-3 text-sm font-medium text-clay hover:bg-clay hover:text-white focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-clay"
          >
            Retry
          </button>
        </p>
      )}

      {!connected && everSynced && (
        <p
          role="status"
          className="rounded-card border border-honey bg-honey-soft px-4 py-3 text-sm text-ink"
        >
          <span className="font-medium">Google isn&rsquo;t connected right now.</span> Events you
          add here are unaffected; the Google mirror just isn&rsquo;t updating.
        </p>
      )}

      {syncResult && (
        // A sync that couldn't read a calendar is a FAILURE, and it says so — same weight
        // as an error, never a quiet grey note under a green tick. Google's own reason is
        // shown verbatim: for a private calendar the usual one is "this calendar was never
        // shared with the service account", and that sentence is the whole fix.
        <div
          role={syncFailed ? 'alert' : 'status'}
          className={
            syncFailed
              ? 'rounded-card border border-clay bg-clay-soft px-4 py-3 text-sm text-ink'
              : 'rounded-card border border-border bg-surface px-4 py-3 text-sm text-ink-soft'
          }
        >
          <p className="flex flex-wrap items-baseline gap-x-2 text-ink">
            {syncFailed && <span className="font-medium text-clay">Sync didn&rsquo;t work.</span>}
            {syncResult.message}
          </p>
          {failedCalendars.length > 0 && (
            <ul className="mt-2 space-y-1.5">
              {failedCalendars.map((cal) => (
                <li key={cal.calendar_id}>
                  <span className="font-mono text-xs font-medium text-ink">
                    {cal.calendar_id}
                  </span>
                  <span className="mt-0.5 block text-ink-soft">{cal.error}</span>
                </li>
              ))}
            </ul>
          )}
          {partialCalendars.length > 0 && (
            // Read, but not all of it. Saying nothing here would let a partial copy pass for
            // a complete one — which is precisely the lie that used to end with real events
            // being deleted from the mirror.
            <ul className="mt-2 space-y-1.5 border-t border-border pt-2">
              {partialCalendars.map((cal) => (
                <li key={cal.calendar_id}>
                  <span className="font-medium text-ink">Partial copy · </span>
                  <span className="font-mono text-xs font-medium text-ink">
                    {cal.calendar_id}
                  </span>
                  <span className="mt-0.5 block text-ink-soft">{cal.error}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      <MemberLegend members={members} />

      <div className="space-y-3">
        <div className="flex items-center gap-2">
          <h2 className="font-display text-lg font-semibold text-ink" aria-live="polite">
            {monthLabel(month)}
          </h2>
          <div className="ml-auto flex items-center gap-1">
            <NavButton
              label="Previous month"
              glyph="‹"
              onClick={() => goToMonth(addMonths(month, -1))}
            />
            <button
              type="button"
              onClick={() => goToMonth(startOfMonth(new Date()))}
              className="min-h-[2.75rem] rounded-full border border-border px-4 text-sm font-medium text-ink-soft hover:bg-surface-sunk hover:text-ink focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sage"
            >
              Today
            </button>
            <NavButton
              label="Next month"
              glyph="›"
              onClick={() => goToMonth(addMonths(month, 1))}
            />
          </div>
        </div>

        <MonthGrid
          month={month}
          eventsByDay={eventsByDay}
          members={members}
          selectedDay={selectedDay}
          onSelectDay={(key) => setSelectedDay((cur) => (cur === key ? null : key))}
        />
      </div>

      <section className="space-y-3">
        <div className="flex flex-wrap items-center gap-2">
          <h2 className="font-display text-lg font-semibold text-ink">
            {selectedDay ? formatDayLabel(selectedDay) : 'Up next'}
          </h2>
          {selectedDay ? (
            <button
              type="button"
              onClick={() => setSelectedDay(null)}
              className="ml-auto min-h-[2.75rem] rounded-full border border-border px-4 text-sm font-medium text-ink-soft hover:bg-surface-sunk hover:text-ink focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sage"
            >
              Back to up next
            </button>
          ) : (
            <span className="ml-auto text-xs text-ink-faint">Next {AGENDA_DAYS} days</span>
          )}
        </div>

        <AgendaList
          events={selectedDay ? dayEvents : upcoming}
          members={members}
          dayScope={selectedDay}
          onEdit={openEdit}
          onDelete={(e) => void handleDelete(e)}
          emptyTitle={selectedDay ? 'Nothing on this day' : "Nothing's coming up"}
          emptyBody={
            selectedDay
              ? 'A clear day. Add something, or tap another day.'
              : `No events in the next ${AGENDA_DAYS} days. Add one to get started.`
          }
          emptyAction={
            <button
              type="button"
              onClick={() => openAdd(selectedDay)}
              className="inline-flex min-h-[2.75rem] items-center gap-1.5 rounded-full bg-sage px-5 text-sm font-medium text-white hover:opacity-90 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sage"
            >
              <span aria-hidden="true" className="text-base leading-none">+</span> Add event
            </button>
          }
        />
      </section>
    </div>
  );
}

// ---------------------------------------------------------------- pieces

function Header({
  status,
  syncing,
  onSync,
  showSync,
}: {
  status: CalendarStatus | null;
  syncing: boolean;
  onSync: () => void;
  showSync: boolean;
}) {
  return (
    <header className="flex flex-wrap items-end gap-3">
      <div>
        <h1 className="font-display text-2xl font-semibold text-ink">Calendar</h1>
        <p className="mt-1 text-sm text-ink-soft">
          {status?.last_synced_at
            ? `The family calendar · also mirroring Google, updated ${formatRelative(status.last_synced_at)}`
            : 'The whole family’s calendar. Add events right here.'}
        </p>
      </div>

      {showSync && (
        <button
          type="button"
          onClick={onSync}
          disabled={syncing}
          className="ml-auto inline-flex min-h-[2.75rem] items-center gap-2 rounded-full border border-border bg-surface px-5 text-sm font-medium text-ink hover:bg-surface-sunk focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sage disabled:opacity-60"
        >
          <span aria-hidden="true" className={syncing ? 'inline-block animate-spin' : 'inline-block'}>
            ↻
          </span>
          {syncing ? 'Syncing…' : 'Sync'}
        </button>
      )}
    </header>
  );
}

function NavButton({
  label,
  glyph,
  onClick,
}: {
  label: string;
  glyph: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={label}
      className="grid h-11 w-11 place-items-center rounded-full border border-border text-lg text-ink-soft hover:bg-surface-sunk hover:text-ink focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sage"
    >
      <span aria-hidden="true">{glyph}</span>
    </button>
  );
}

function CalendarSkeleton() {
  return (
    <div className="space-y-6" aria-busy="true" aria-live="polite">
      <span className="sr-only">Loading the calendar…</span>
      <div className="h-8 w-40 animate-pulse rounded bg-surface-sunk" />
      <div className="grid grid-cols-7 gap-px overflow-hidden rounded-card border border-border bg-border">
        {Array.from({ length: 42 }).map((_, i) => (
          <div key={i} className="h-[3.25rem] animate-pulse bg-surface sm:h-24" />
        ))}
      </div>
      <div className="space-y-2">
        {Array.from({ length: 3 }).map((_, i) => (
          <div key={i} className="h-16 animate-pulse rounded-card bg-surface-sunk" />
        ))}
      </div>
    </div>
  );
}
